import { formSingleOptionData, formSingleContentData } from 'state/reducers/embedsState/embedManagementState/contentStatus/content_status_state_structure';


// HELPER METHODS TO FORM A DEFAULT STATE DATA STRUCTURE

export const hasQBData = contentData => !!contentData && (contentData.contentType === 'QUIZ' || contentData.contentType === 'POLL');
export const hasStoriesData = contentData => !!contentData && contentData.contentType === 'STORY';

// Forms a status blob for all of the question options
// For new question forms 3 options
export const formOptionsData = optionsData => {
    const options = {};
    for (let idx=0; idx < (optionsData ? Object.keys(optionsData).length : 3); idx++) {
        let optionId = optionsData ? Object.keys(optionsData)[idx] : `${idx}`;
        options[optionId] = formSingleOptionData(optionsData ? optionsData[optionId] : null, idx);
    }
    return options;
};


/* ++++++++++ --------------- CUSTOM METHODS --------------- ++++++++++ */

// TRANSPILER
// Updates content status with the data received from the BE.
// Updates some of the contentStatus properties to account for received contents;
export const fromContentsData = contentsData => {
    const contents = {};
    let lastQueuePosition = 0;
    let totalNumberOfContents = 0;
    let firstLiveContentId = '0';
    let totalNumberOfLiveContents = 0;
    for(let contentId of Object.keys(contentsData)) {
        lastQueuePosition = contentsData[contentId].queuePosition !== null && contentsData[contentId].queuePosition > lastQueuePosition ? contentsData[contentId].queuePosition : lastQueuePosition;
        totalNumberOfContents += 1;
        firstLiveContentId = contentsData[contentId].queuePosition === 0 ? contentId : firstLiveContentId;
        contents[contentId] = formSingleContentData({ contentData: contentsData[contentId], contentProperties: contentsData[contentId]['contentProperties'], contentId, type: null, position: null });
        if (contentsData[contentId]['contentLive']) { totalNumberOfLiveContents += 1; }
    }
    let noLiveContents = totalNumberOfLiveContents === 0;
    return { contents, lastQueuePosition, totalNumberOfContents, firstLiveContentId, noLiveContents };
};


export const invalidateContent = contentData => contentData.contentType === 'story' ?
    _invalidateStoryContent(contentData) : _invalidateQBContent(contentData);

const _invalidateQBContent = contentData => {
    const content = JSON.parse(JSON.stringify(contentData));
    content.questionBased.isValid = false; // this method is called when we know that the content is not valid
    // isValid will be set back to null when content will be selected
    content.questionBased.isTitleValid = !!content.questionBased.isTitleValid; // will set content.isTitleValid to false if it is set to null
    content.questionBased.correctOptionSelected = content.contentType !== 'quiz'; // validation for correct option selection. This validation is not happening in individual components.
    for(let contentId of Object.keys(content.questionBased.options)) {
        content.questionBased.options[contentId]['isValid'] = content.questionBased.options[contentId]['isValid'] instanceof Object ? false : !!content.questionBased.options[contentId]['isValid'];
        content.questionBased.correctOptionSelected = content.contentType === 'quiz' && content.questionBased.options[contentId]['correctOption'] ?
            true : content.questionBased.correctOptionSelected;
    }
    return content;
};

const _invalidateStoryContent = contentData => {
    const content = JSON.parse(JSON.stringify(contentData));
    content.story.isValid = false; // this method is called when we know that the content is not valid
    // isValid will be set back to null when content will be selected
    content.story.isTitleValid = !!content.story.isTitleValid; // will set content.story.isTitleValid to false if it is set to null
    content.story.isURLValid = !!content.story.isURLValid;     // will set content.story.isURLValid to false if it is set to null
    return content;
};

export const getUpdatedStateParamsOnDelete = (contentsData, removedContentId, removedQueuePosition) =>{
    let lastQueuePosition = 0;
    let newSelectedContent = '0';
    const contents = {};
    for(let contentId of Object.keys(contentsData)) {
        contents[contentId] = contentsData[contentId];
        contents[contentId]['queuePosition'] = contentsData[contentId].queuePosition !== null ? contentsData[contentId].queuePosition < removedQueuePosition ? contentsData[contentId].queuePosition : contentsData[contentId].queuePosition - 1 : null;
        lastQueuePosition = contents[contentId].queuePosition !== null && contents[contentId].queuePosition > lastQueuePosition ? contents[contentId].queuePosition : lastQueuePosition;
        newSelectedContent = contents[contentId].queuePosition !== null && contents[contentId].queuePosition === lastQueuePosition ? contentId : newSelectedContent;
    }
    return { lastQueuePosition, newSelectedContent, contents }
};

export const setSelectedImage = (state, action) => {
    const newState = JSON.parse(JSON.stringify(state));
    const contentType = newState['contents'][newState.selectedContent]['contentType'] === 'story' ? 'story' : 'questionBased';
    newState['contentChangeDetected'] = true;
    switch (newState['activeSelection']) {
        case 'background':
            newState['contents'][newState.selectedContent][contentType]['imageProvider'] = action.provider;
            newState['contents'][newState.selectedContent][contentType]['imageID'] = action.imageId;
            newState['contents'][newState.selectedContent][contentType]['backgroundImageURL'] = action.imageURL;
            newState['contents'][newState.selectedContent][contentType]['previewBackgroundImageURL'] = null;
            newState['activeSelection'] = 'none';
            return newState;
        default:
            if (newState['activeSelection'].includes('option')) {
                const optionId = newState['activeSelection'].slice(7);
                newState['contents'][newState.selectedContent]['questionBased']['options'][optionId]['imageProvider'] = action.provider;
                newState['contents'][newState.selectedContent]['questionBased']['options'][optionId]['imageID'] = action.imageId;
                newState['contents'][newState.selectedContent]['questionBased']['options'][optionId]['imageURL'] = action.imageURL;
                newState['contents'][newState.selectedContent]['questionBased']['options'][optionId]['previewImageURL']= null;
                newState['activeSelection'] = 'none';
                return newState;
            }
            return state;
    }
};
export const setPreviewImage = (state, action) => {
    const newState = JSON.parse(JSON.stringify(state));
    const contentType = newState['contents'][newState.selectedContent]['contentType'] === 'story' ? 'story' : 'questionBased';
    switch (newState['activeSelection']) {
        case 'background':
            newState['contents'][newState.selectedContent][contentType]['previewBackgroundImageURL'] = action.imageURL;
            return newState;
        default:
            if (newState['activeSelection'].includes('option')) {
                const optionId = newState['activeSelection'].slice(7);
                newState['contents'][newState.selectedContent]['questionBased']['options'][optionId]['previewImageURL'] = action.imageURL;
                return newState;
            }
            return state;
    }
};

export const typeChangedFromOriginal = (originalType, currentType, newType) =>
    // Ignore type change for a new content.
    originalType === null ? false :
    // 1. Content type has actually changed. New type is different from the current one.
    contentTypeChanged(currentType, newType) &&
    // 2. Current type was the original one.
    !contentTypeChanged(originalType, currentType);

export const typeChangedToOriginal = (originalType, currentType, newType) =>
    // Ignore type change for a new content.
    originalType === null ? false :
    // 1. Content type has actually changed. New type is different from the current one.
    contentTypeChanged(currentType, newType) &&
    // 2. New content type is the original one
    !contentTypeChanged(originalType, newType);


export const contentTypeChanged = (baseType, contentType) => {
    switch (baseType) {
        case 'story':
            return contentType !== 'story';
        case 'poll':
        // fall through
        case 'quiz':
            return contentType === 'story';
        default:
            return false;
    }
};

export const getStateWithOfflineContent = (state, prevState) => {
    const newState = JSON.parse(JSON.stringify(state));
    const previousState = JSON.parse(JSON.stringify(prevState));

    // content state with all of the modifications. Properties of both questionBased and story types are populated.
    // currentSelectedContentState is a reference.
    const currentSelectedContentState = newState['contents'][newState.selectedContent];

    // generate a new id to store a state of the originally received content separately from an updated content state
    const newContentId = generateId();

    // set originally received content offline
    const originalContentState = previousState['contents'][newState.selectedContent];
    originalContentState['contentLive'] = false;
    originalContentState['queuePosition'] = null;


    // set id's to map the existing and a newly created contents
    originalContentState['parentId'] = newState.selectedContent;
    currentSelectedContentState['offlineBlobId'] = newContentId;


    // add stored copy of the the originally received content to the list of contents
    newState['contents'][newContentId] = originalContentState;

    return newState;
};

export const finalizeContentTypeChange = state => {
    const newState = JSON.parse(JSON.stringify(state));
    const originalType = newState['contents'][newState.selectedContent]['originalType'];

    // content state with all of the modifications. Properties of both questionBased and story types are populated.
    // currentSelectedContentState is a reference.
    const currentSelectedContentState = newState['contents'][newState.selectedContent];

    // clear properties of the original content type
    const origialTypeSpecificProps =  _isQuestionBased(originalType) ? 'questionBased': 'story';
    currentSelectedContentState[origialTypeSpecificProps] = formSingleContentData({ contentData: null, contentProperties: null, contentId: null, type: 'quiz', position: 0 })[origialTypeSpecificProps];

    // remove the connection between the currently chenged content and its offline blob
    const offlineBlobId = currentSelectedContentState['offlineBlobId'];
    newState['contents'][offlineBlobId]['parentId'] = null;
    currentSelectedContentState['offlineBlobId'] = null;
    currentSelectedContentState['originalType'] = null;

    return newState;
};

export const removeOfflineBlob = state => {
    const newState = JSON.parse(JSON.stringify(state));
    const offlineBlobId = newState['contents'][newState.selectedContent]['offlineBlobId'];
    // currentSelectedContentState is a reference.
    const currentSelectedContentState = newState['contents'][newState.selectedContent];

    currentSelectedContentState['offlineBlobId'] = null;
    delete newState['contents'][offlineBlobId];
    return newState;
};

export const seletedContentTypeChanged = selectedContentState => !!selectedContentState && !!selectedContentState.offlineBlobId && selectedContentState.originalType;

const _isQuestionBased = contentType => ['poll', 'quiz'].includes(contentType);

export const generateId = () => {
    return ([1e7] + -1e3).replace(/[018]/g, c =>
        (c ^ crypto.getRandomValues(new Uint8Array(1))[0] & 15 >> c / 2).toString(16)
    )
};

export const noLiveContentCheck = content => {
    const liveContent = [];
    Object.keys(content).forEach(item => {
        if(content[item].contentLive){
            liveContent.push(item)
        }
    });
    return liveContent.length < 1
};
